import React, { ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import { Sidebar } from 'primereact/sidebar';
import { Skeleton } from 'primereact/skeleton';
import MultiSelectBox from '@Common/MultiSelectBoxV6/MultiSelectBoxV6';
import Notification from '@Common/Notification';
import Button from '@Common/Button';
import * as types from '@Constants/actionTypes';
import { actions as locationTaskActions } from '@Actions/locationTaskActions';
import { DrawerFilters } from '../LocationTaskSidebar/Types';
import './QuickCreateTaskSidebar.scss';
import { Option } from '@Types/common';

interface QuickCreateTaskSidebarProps {
    isDrawerVisible: boolean;
    closeDrawer: () => void;
    onTaskCreated: (taskId: number, locationKey: string) => void;
    locationKey: string;
    userId: number;
    currentProjectIds: number[];
    currentDepartmentIds: number[];
    responsibleUsers: Array<Option>;
    // Edit mode: when provided and > 0, the sidebar pre-loads the task for editing
    editLocationTaskId?: number;
    onTaskUpdated?: (taskId: number, locationKey: string) => void;
}

interface FormData {
    name: string;
    additionalInfo: string;
    startDate: string;
    targetDate: string;
    responsibleUserId: string;
    statusId: number;
    departmentId: string;
    communityId: string;
    projectId: string;
    contingentId: string;
    successorId: string;
    phaseId: string;
    contactName: string;
    contactEmail: string;
    isMilestone: boolean;
}

const getDefaultTargetDate = (): string =>
    moment().add(30, 'days').format('YYYY-MM-DD');

const EMPTY_FILTERS: DrawerFilters = {
    isStartDateActive: false,
    isTaskStepsActive: false,
    isProjectWidgetActive: false,
    isProjectIdRequiredForLocationTask: false,
    isTaskDependenciesActive: false,
    defaultNotStartedStatusId: 0,
    allowLocationTaskDateChanges: true,
    completeStatusId: 0,
    locationOpeningDate: '',
    statuses: [],
    responsibleUsers: [],
    departments: [],
    communities: [],
    projects: [],
    phases: [],
    contingentTasks: [],
    taskTags: [],
};

const QuickCreateTaskSidebar = (props: QuickCreateTaskSidebarProps): ReactElement => {
    const isEditMode = (props.editLocationTaskId ?? 0) > 0;
    const [isVisible, setIsVisible] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isFormValid, setIsFormValid] = useState<boolean>(true);
    const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
    const [drawerFilters, setDrawerFilters] = useState<DrawerFilters>(EMPTY_FILTERS);
    const [defaultStatusId, setDefaultStatusId] = useState<number>(0);
    const [editTaskInternalId, setEditTaskInternalId] = useState<number>(0);
    const [formData, setFormData] = useState<FormData>({
        name: '',
        additionalInfo: '',
        startDate: '',
        targetDate: getDefaultTargetDate(),
        responsibleUserId: props.userId?.toString() || '',
        departmentId: '',
        communityId: '',
        projectId: '',
        contingentId: '',
        statusId: 0,
        successorId: '',
        phaseId: '',
        contactName: '',
        contactEmail: '',
        isMilestone: false,
    });

    const dispatch: Dispatch = useDispatch();

    const getLocationTaskFilters = (locationKey: string) =>
        dispatch<any>(locationTaskActions.loadLocationTaskDrawerFilters(0, locationKey));
    const createTask = (request: any) =>
        dispatch<any>(locationTaskActions.createLocationTask(request));
    const getLocationTask = (locationTaskId: number) =>
        dispatch<any>(locationTaskActions.loadLocationTask(locationTaskId));
    const saveTask = (request: any) =>
        dispatch<any>(locationTaskActions.saveLocationTaskEditMode(request));

    // Reset form and load filters (and task data in edit mode) when drawer opens
    // or when the target task/location changes while the drawer is already visible
    useEffect(() => {
        if (props.isDrawerVisible) {
            resetForm();
            if (props.locationKey) {
                loadFilters();
            }
            if (isEditMode && props.editLocationTaskId) {
                loadEditTask(props.editLocationTaskId);
            }
        }
    }, [props.isDrawerVisible, props.editLocationTaskId, props.locationKey]);

    // Set smart defaults once filters are loaded
    useEffect(() => {
        if (!props.isDrawerVisible) return;
        if (!drawerFilters.departments.length && !drawerFilters.communities.length) return;

        setFormData(prev => {
            const next = { ...prev };

            // Responsible user: default to logged-in user
            if (!next.responsibleUserId && props.userId) {
                next.responsibleUserId = props.userId.toString();
            }

            // Department: if page is filtered by dept → match; otherwise first dept from filters
            if (!next.departmentId) {
                if (props.currentDepartmentIds?.length > 0) {
                    const match = drawerFilters.departments.find(d =>
                        props.currentDepartmentIds.includes(d.id as number));
                    next.departmentId = (match ?? drawerFilters.departments[0])?.id?.toString() || '';
                } else {
                    next.departmentId = drawerFilters.departments[0]?.id?.toString() || '';
                }
            }

            // Community: first accessible community from filters
            if (!next.communityId && drawerFilters.communities.length > 0) {
                next.communityId = drawerFilters.communities[0]?.id?.toString() || '';
            }

            // Project: if page is filtered by project → match; otherwise blank
            if (!next.projectId && props.currentProjectIds?.length > 0) {
                const match = drawerFilters.projects.find(p =>
                    props.currentProjectIds.includes(p.id as number));
                if (match) {
                    next.projectId = match.id.toString();
                }
            }

            return next;
        });
    }, [drawerFilters]);

    const resetForm = () => {
        setFormData({
            name: '',
            additionalInfo: '',
            startDate: '',
            targetDate: getDefaultTargetDate(),
            responsibleUserId: props.userId?.toString() || '',
            departmentId: '',
            statusId: 0,
            communityId: '',
            projectId: '',
            contingentId: '',
            successorId: '',
            phaseId: '',
            contactName: '',
            contactEmail: '',
            isMilestone: false,
        });
        setDrawerFilters(EMPTY_FILTERS);
        setIsFormValid(true);
        setIsAdvancedOpen(false);
    };

    const loadEditTask = async (locationTaskId: number) => {
        setIsLoading(true);
        try {
            const response = await getLocationTask(locationTaskId);
            if (response.type === types.LOAD_LOCATION_TASK_SUCCESS) {
                const task = response.payload?.locationTask as any;
                setEditTaskInternalId(task.id);
                setFormData(prev => ({
                    ...prev,
                    name: task.name || '',
                    additionalInfo: task.additionalInfo || '',
                    startDate: task.startDate ? moment(task.startDate).format('YYYY-MM-DD') : '',
                    targetDate: task.targetDate ? moment(task.targetDate).format('YYYY-MM-DD') : '',
                    responsibleUserId: task.responsibleUserId?.toString() || '',
                    departmentId: task.departmentId?.toString() || '',
                    communityId: task.communityId?.toString() || '',
                    projectId: task.projectId?.toString() || '',
                    contingentId: task.contingentId?.toString() || '',
                    phaseId: task.phaseId?.toString() || '',
                    contactName: task.contactName || '',
                    contactEmail: task.contactEmail || '',
                    isMilestone: task.isMilestone || false,
                    statusId: task.statusId || 0,
                }));
            } else if (response.type === types.LOAD_LOCATION_TASK_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadFilters = async () => {
        setIsLoading(true);
        try {
            const response = await getLocationTaskFilters(props.locationKey);
            if (response.type === types.LOAD_LOCATION_TASK_DRAWER_FILTERS_SUCCESS) {
                setDrawerFilters(response.payload as DrawerFilters);
                setDefaultStatusId((response.payload as DrawerFilters).defaultNotStartedStatusId || 0);
            } else if (response.type === types.LOAD_LOCATION_TASK_DRAWER_FILTERS_FAILURE) {
                Notification('error', 'Error', response.payload.message);
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const responsibleId = parseInt(formData.responsibleUserId);
        const validProject = !drawerFilters.isProjectIdRequiredForLocationTask
            || (drawerFilters.isProjectIdRequiredForLocationTask && formData.projectId.trim().length > 0);

        return (
            formData.name.trim().length > 0 &&
            formData.targetDate.trim().length > 0 &&
            moment(formData.targetDate, 'YYYY-MM-DD', true).isValid() &&
            responsibleId > 0 &&
            formData.departmentId.trim().length > 0 &&
            formData.communityId.trim().length > 0 &&
            validProject
        );
    };

    const onCreateTask = async () => {
        if (!validateForm()) {
            setIsFormValid(false);
            return;
        }

        setIsSaving(true);
        try {
            if (isEditMode) {
                const request = {
                    id: editTaskInternalId,
                    locationTaskId: editTaskInternalId,
                    name: formData.name.trim(),
                    additionalInfo: formData.additionalInfo.trim(),
                    startDate: formData.startDate ? new Date(formData.startDate) : null,
                    targetDate: new Date(formData.targetDate),
                    responsibleUserId: parseInt(formData.responsibleUserId),
                    departmentId: formData.departmentId ? parseInt(formData.departmentId) : 0,
                    communityId: formData.communityId ? parseInt(formData.communityId) : 0,
                    projectId: formData.projectId ? parseInt(formData.projectId) : null,
                    contingentId: formData.contingentId ? parseInt(formData.contingentId) : null,
                    successorId: formData.successorId ? parseInt(formData.successorId) : null,
                    phaseId: formData.phaseId ? parseInt(formData.phaseId) : null,
                    contactName: formData.contactName.trim() || null,
                    contactEmail: formData.contactEmail.trim() || null,
                    isMilestone: formData.isMilestone,
                    locationKey: props.locationKey,
                    statusId: formData.statusId,
                };
                const response = await saveTask(request);
                if (response.type === types.UPDATE_LOCATION_TASK_EDIT_MODE_SUCCESS) {
                    Notification('success', 'Success', 'Task updated successfully');
                    props.onTaskUpdated?.(editTaskInternalId, props.locationKey);
                } else if (response.type === types.UPDATE_LOCATION_TASK_EDIT_MODE_FAILURE) {
                    Notification('error', 'Error', response.payload.message);
                }
            } else {
                const request = {
                    name: formData.name.trim(),
                    additionalInfo: formData.additionalInfo.trim(),
                    startDate: formData.startDate ? new Date(formData.startDate) : null,
                    targetDate: new Date(formData.targetDate),
                    responsibleUserId: parseInt(formData.responsibleUserId),
                    departmentId: formData.departmentId ? parseInt(formData.departmentId) : 0,
                    communityId: formData.communityId ? parseInt(formData.communityId) : 0,
                    projectId: formData.projectId ? parseInt(formData.projectId) : null,
                    contingentId: formData.contingentId ? parseInt(formData.contingentId) : null,
                    successorId: formData.successorId ? parseInt(formData.successorId) : null,
                    phaseId: formData.phaseId ? parseInt(formData.phaseId) : null,
                    contactName: formData.contactName.trim() || null,
                    contactEmail: formData.contactEmail.trim() || null,
                    isMilestone: formData.isMilestone,
                    locationKey: props.locationKey,
                    statusId: defaultStatusId || 0,
                    isActive: true,
                    links: [],
                };
                const response = await createTask(request);
                if (response.type === types.CREATE_LOCATION_TASK_SUCCESS) {
                    Notification('success', 'Success', 'Task created successfully');
                    props.onTaskCreated(response.payload.locationTaskId, props.locationKey);
                } else if (response.type === types.CREATE_LOCATION_TASK_FAILURE) {
                    Notification('error', 'Error', response.payload.message);
                }
            }
        } catch (error) {
            Notification('error', 'Error', error);
        } finally {
            setIsSaving(false);
        }
    };

    const updateField = (field: keyof FormData, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (!isFormValid) setIsFormValid(true);
    };

    const onShowSidebar = () => setIsVisible(true);
    const onHideSidebar = () => {
        setIsVisible(false);
        props.closeDrawer();
    };

    const requiredStar = <span className="required-star"> *</span>;

    const sectionHeader = (icon: React.ReactNode, title: string, optional = false) => (
        <div className="form-section-header">
            {icon}
            {title}
            {optional && <span className="optional-label">(optional)</span>}
        </div>
    );

    const fieldError = (msg: string) => (
        <span className="form-field-error">{msg}</span>
    );

    // ── Sidebar content ────────────────────────────────────────────────────────
    const sidebarContent = () => (
        <>
            {/* Header */}
            <div className="card-header" style={{
                backgroundColor: '#f6f9fc',
                padding: '16px 24px',
                minHeight: '60px',
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #dee2e6',
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                    <span style={{
                        fontFamily: 'Greycliff',
                        fontWeight: 700,
                        color: '#343a40',
                        fontSize: '16px',
                        lineHeight: '24px',
                        flex: 1,
                    }}>
                        {isEditMode ? 'Edit Task' : 'Create New Task'}
                    </span>
                    <button
                        type="button"
                        onClick={props.closeDrawer}
                        style={{
                            padding: '8px',
                            background: 'none',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                        }}
                        onMouseOver={e => (e.currentTarget.style.backgroundColor = '#dee2e6')}
                        onMouseOut={e => (e.currentTarget.style.backgroundColor = 'transparent')}
                        aria-label="Close"
                    >
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ color: '#6c757d' }}>
                            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                        </svg>
                    </button>
                </div>
            </div>

            {/* Body */}
            {isLoading ? (
                <div style={{ padding: '24px' }}>
                    <Skeleton width="100%" height="40px" className="mb-3" />
                    <Skeleton width="100%" height="80px" className="mb-3" />
                    <Skeleton width="100%" height="40px" className="mb-3" />
                    <Skeleton width="100%" height="40px" className="mb-3" />
                    <Skeleton width="100%" height="40px" className="mb-3" />
                </div>
            ) : (
                <div className="quick-create-form">
                    {/* Task Name */}
                    <div className="form-field">
                        <label className="form-label">
                            Task Name{requiredStar}
                        </label>
                        <input
                            type="text"
                            placeholder="What needs to be completed?"
                            value={formData.name}
                            onChange={e => updateField('name', e.target.value)}
                            autoFocus
                            className={`form-input${!isFormValid && !formData.name.trim() ? ' is-invalid' : ''}`}
                        />
                        {!isFormValid && !formData.name.trim() && fieldError('Task name is required')}
                    </div>

                    {/* Additional Information */}
                    <div className="form-field">
                        <label className="form-label">Additional Information</label>
                        <textarea
                            placeholder="How should the task be completed and its impact?"
                            value={formData.additionalInfo}
                            onChange={e => updateField('additionalInfo', e.target.value)}
                            rows={3}
                            className="form-input"
                        />
                    </div>

                    {/* Dates row */}
                    <div className="form-row">
                        {/* Start By Date */}
                        <div>
                            <label className="form-label">
                                Start By Date{' '}
                                <span style={{ color: '#9ca3af', fontWeight: 400 }}>(optional)</span>
                            </label>
                            <DatePicker
                                selected={formData.startDate ? moment(formData.startDate, 'YYYY-MM-DD').toDate() : null}
                                onChange={(date: Date | null) => updateField('startDate', date ? moment(date).format('YYYY-MM-DD') : '')}
                                dateFormat="d MMM yyyy"
                                placeholderText="Select start date..."
                                className="form-input"
                                wrapperClassName="w-100"
                                isClearable
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                            />
                        </div>

                        {/* Target Date */}
                        <div>
                            <label className="form-label">Target Date{requiredStar}</label>
                            <DatePicker
                                selected={formData.targetDate ? moment(formData.targetDate, 'YYYY-MM-DD').toDate() : null}
                                onChange={(date: Date | null) => updateField('targetDate', date ? moment(date).format('YYYY-MM-DD') : '')}
                                dateFormat="d MMM yyyy"
                                placeholderText="Select target date..."
                                className={`form-input${!isFormValid && !formData.targetDate ? ' is-invalid' : ''}`}
                                wrapperClassName="w-100"
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                            />
                            {!isFormValid && !formData.targetDate && fieldError('Target date is required')}
                        </div>
                    </div>

                    {/* Department & Responsible row */}
                    <div className="form-row">
                        {/* Department */}
                        <div>
                            <label className="form-label">
                                <svg width="13" height="13" viewBox="0 0 15 15" fill="none"
                                    style={{ color: '#6c757d', marginRight: '4px', verticalAlign: 'middle' }}>
                                    <path d="M5 5V3.5C5 3 5.4 2.5 6 2.5H9C9.6 2.5 10 3 10 3.5V5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                    <rect x="1.5" y="5" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.3" />
                                    <path d="M1.5 8.5H13.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                </svg>
                                Department{requiredStar}
                            </label>
                            <MultiSelectBox
                                useFixedMenuPosition
                                className='w-100'
                                name='departmentId'
                                idField='id'
                                valueField='name'
                                options={drawerFilters.departments}
                                onChange={(value: string) => updateField('departmentId', value)}
                                selectedValues={formData.departmentId}
                                isMulti={false}
                                isSearchable={false}
                                placeholder="Select department..."
                            />
                            {!isFormValid && !formData.departmentId && fieldError('Department is required')}
                        </div>

                        {/* Responsible */}
                        <div>
                            <label className="form-label">
                                <svg width="13" height="13" viewBox="0 0 15 15" fill="none"
                                    style={{ color: '#6c757d', marginRight: '4px', verticalAlign: 'middle' }}>
                                    <circle cx="7.5" cy="4.5" r="2.5" stroke="currentColor" strokeWidth="1.3" />
                                    <path d="M2.5 13C2.5 10.5 4.5 9 7.5 9C10.5 9 12.5 10.5 12.5 13"
                                        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                </svg>
                                Responsible{requiredStar}
                            </label>
                            <MultiSelectBox
                                useFixedMenuPosition
                                className='w-100'
                                name='responsibleUserId'
                                idField='id'
                                valueField='name'
                                options={props.responsibleUsers}
                                onChange={(value: string) => updateField('responsibleUserId', value)}
                                selectedValues={formData.responsibleUserId}
                                isMulti={false}
                                isSearchable={false}
                                placeholder="Select responsible user..."
                            />
                            {!isFormValid && !formData.responsibleUserId && fieldError('Responsible user is required')}
                        </div>
                    </div>

                    {/* Project & Community row */}
                    <div className="form-row">
                        {/* Project */}
                        <div>
                            <label className="form-label">
                                <svg width="13" height="13" viewBox="0 0 15 15" fill="none"
                                    style={{ color: '#6c757d', marginRight: '4px', verticalAlign: 'middle' }}>
                                    <path d="M1.5 4C1.5 3.4 2 3 2.5 3H5.5L7 5H12.5C13 5 13.5 5.4 13.5 6V11C13.5 11.6 13 12 12.5 12H2.5C2 12 1.5 11.6 1.5 11V4Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                                </svg>
                                Project{drawerFilters.isProjectIdRequiredForLocationTask ? requiredStar : (
                                    <span style={{ color: '#9ca3af', fontWeight: 400 }}> (optional)</span>
                                )}
                            </label>
                            <MultiSelectBox
                                useFixedMenuPosition
                                className='w-100'
                                name='projectId'
                                idField='id'
                                valueField='name'
                                options={drawerFilters.projects}
                                onChange={(value: string) => updateField('projectId', value)}
                                selectedValues={formData.projectId}
                                isMulti={false}
                                isSearchable={true}
                                placeholder="Select project..."
                            />
                            {!isFormValid && drawerFilters.isProjectIdRequiredForLocationTask && !formData.projectId
                                && fieldError('Project is required')}
                        </div>

                        {/* Community */}
                        <div>
                            <label className="form-label">
                                <svg width="13" height="13" viewBox="0 0 15 15" fill="none"
                                    style={{ color: '#6c757d', marginRight: '4px', verticalAlign: 'middle' }}>
                                    <circle cx="5" cy="5" r="2" stroke="currentColor" strokeWidth="1.3" />
                                    <circle cx="10" cy="5" r="2" stroke="currentColor" strokeWidth="1.3" />
                                    <path d="M1 13C1 10.8 2.8 9.5 5 9.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                    <path d="M6.5 13C6.5 10.8 8 9.5 10 9.5C12 9.5 14 10.8 14 13"
                                        stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                </svg>
                                Who can see this task?{requiredStar}
                            </label>
                            <MultiSelectBox
                                useFixedMenuPosition
                                className='w-100'
                                name='communityId'
                                idField='id'
                                valueField='name'
                                options={drawerFilters.communities}
                                onChange={(value: string) => updateField('communityId', value)}
                                selectedValues={formData.communityId}
                                isMulti={false}
                                isSearchable={false}
                                placeholder="Select community..."
                            />
                            {!isFormValid && !formData.communityId && fieldError('Community is required')}
                        </div>
                    </div>

                    {/* Advanced Options — collapsible */}
                    <div className="form-section-collapsible">
                        <button
                            type="button"
                            className="form-section-toggle"
                            onClick={() => setIsAdvancedOpen(prev => !prev)}
                            aria-expanded={isAdvancedOpen}
                        >
                            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ color: '#6c757d' }}>
                                <path d="M3 3H12M3 7H12M3 11H8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                <circle cx="12" cy="11" r="2" stroke="currentColor" strokeWidth="1.3" />
                            </svg>
                            <span>Advanced Options</span>
                            <span className="optional-label">(optional)</span>
                            <svg
                                width="14" height="14" viewBox="0 0 14 14" fill="none"
                                className="form-section-toggle-chevron"
                                style={{ transform: isAdvancedOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
                            >
                                <path d="M2 5L7 10L12 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>

                        {isAdvancedOpen && (
                            <>
                                {/* Pre-requisites section */}
                                <div className="form-section">
                                    {sectionHeader(
                                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ color: '#6c757d' }}>
                                            <path d="M3 3H12M3 7H12M3 11H8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                            <circle cx="12" cy="11" r="2" stroke="currentColor" strokeWidth="1.3" />
                                            <path d="M12 9V11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                        </svg>,
                                        'Pre-requisites',
                                        true,
                                    )}

                                    <div className="form-grid">
                                        {/* Upstream */}
                                        <div>
                                            <label className="form-label">
                                                <svg width="13" height="13" viewBox="0 0 15 15" fill="none"
                                                    style={{ color: '#6c757d', marginRight: '4px', verticalAlign: 'middle' }}>
                                                    <path d="M10 7.5H2M2 7.5L5 5M2 7.5L5 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M13 4V11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                                </svg>
                                                Upstream task
                                            </label>
                                            <MultiSelectBox
                                                useFixedMenuPosition
                                                className='w-100'
                                                name='contingentId'
                                                idField='id'
                                                valueField='name'
                                                options={drawerFilters.contingentTasks}
                                                onChange={(value: string) => updateField('contingentId', value)}
                                                selectedValues={formData.contingentId}
                                                isMulti={false}
                                                isSearchable={true}
                                                placeholder="Start typing the task name to filter..."
                                            />
                                            <span className="form-helper-text">
                                                The selected task needs to be done first.
                                            </span>
                                        </div>

                                        {/* Downstream */}
                                        {!isEditMode && <div>
                                            <label className="form-label">
                                                <svg width="13" height="13" viewBox="0 0 15 15" fill="none"
                                                    style={{ color: '#6c757d', marginRight: '4px', verticalAlign: 'middle' }}>
                                                    <path d="M5 7.5H13M13 7.5L10 5M13 7.5L10 10" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                                                    <path d="M2 4V11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                                </svg>
                                                Downstream task
                                            </label>
                                            <MultiSelectBox
                                                useFixedMenuPosition
                                                className='w-100'
                                                name='successorId'
                                                idField='id'
                                                valueField='name'
                                                options={drawerFilters.contingentTasks}
                                                onChange={(value: string) => updateField('successorId', value)}
                                                selectedValues={formData.successorId}
                                                isMulti={false}
                                                isSearchable={true}
                                                placeholder="Start typing the task name to filter..."
                                            />
                                            <span className="form-helper-text">
                                                The selected task needs to be done next.
                                            </span>
                                        </div>}
                                    </div>
                                </div>

                                {/* Optional Details section */}
                                <div className="form-section">
                                    {sectionHeader(
                                        <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ color: '#6c757d' }}>
                                            <circle cx="7.5" cy="7.5" r="6.5" stroke="currentColor" strokeWidth="1.3" />
                                            <path d="M7.5 5V7.5M7.5 10V10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                        </svg>,
                                        'Optional Details',
                                    )}

                                    <div className="form-grid">
                                        {/* Phase */}
                                        <div>
                                            <label className="form-label">
                                                <svg width="13" height="13" viewBox="0 0 15 15" fill="none"
                                                    style={{ color: '#6c757d', marginRight: '4px', verticalAlign: 'middle' }}>
                                                    <path d="M2 3H10L8.5 5.5L10 8H2V3Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                                                    <path d="M2 3V13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
                                                </svg>
                                                Phase
                                            </label>
                                            <MultiSelectBox
                                                useFixedMenuPosition
                                                className='w-100'
                                                name='phaseId'
                                                idField='id'
                                                valueField='name'
                                                options={drawerFilters.phases}
                                                onChange={(value: string) => updateField('phaseId', value)}
                                                selectedValues={formData.phaseId}
                                                isMulti={false}
                                                isSearchable={false}
                                                placeholder="Select a phase..."
                                            />
                                        </div>

                                        {/* Milestone toggle */}
                                        <div>
                                            <label className="form-label">Milestone</label>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', height: '38px' }}>
                                                <div
                                                    role="switch"
                                                    aria-checked={formData.isMilestone}
                                                    onClick={() => updateField('isMilestone', !formData.isMilestone)}
                                                    style={{
                                                        width: '44px',
                                                        height: '24px',
                                                        borderRadius: '12px',
                                                        backgroundColor: formData.isMilestone ? '#5341c6' : '#dee2e6',
                                                        position: 'relative',
                                                        cursor: 'pointer',
                                                        transition: 'background-color 0.2s ease',
                                                        flexShrink: 0,
                                                    }}
                                                >
                                                    <div style={{
                                                        position: 'absolute',
                                                        top: '2px',
                                                        left: formData.isMilestone ? '22px' : '2px',
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        backgroundColor: 'white',
                                                        transition: 'left 0.2s ease',
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                                    }} />
                                                </div>
                                                <span style={{
                                                    fontFamily: 'Greycliff',
                                                    fontSize: '14px',
                                                    color: '#31363a',
                                                    fontWeight: 500,
                                                }}>
                                                    {formData.isMilestone ? 'Yes' : 'No'}
                                                </span>
                                            </div>
                                        </div>

                                        {/* Contact for Assistance */}
                                        <div>
                                            <label className="form-label">Contact for Assistance</label>
                                            <input
                                                type="text"
                                                placeholder="Enter contact name..."
                                                value={formData.contactName}
                                                onChange={e => updateField('contactName', e.target.value)}
                                                className="form-input"
                                            />
                                        </div>

                                        {/* Contact Email */}
                                        <div>
                                            <label className="form-label">Contact Email</label>
                                            <input
                                                type="email"
                                                placeholder="contact@example.com"
                                                value={formData.contactEmail}
                                                onChange={e => updateField('contactEmail', e.target.value)}
                                                className="form-input"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Footer */}
            <div className="border-top location-task-sidebar-footer">
                <Button
                    type="button"
                    text="Cancel"
                    bootstrapClass="btn btn-link"
                    onClick={props.closeDrawer}
                    isDisabled={isSaving}
                />
                <Button
                    type="button"
                    text={isSaving ? (isEditMode ? 'Saving...' : 'Creating...') : (isEditMode ? 'Save Changes' : 'Create Task')}
                    bootstrapClass="btn btn-primary"
                    onClick={onCreateTask}
                    isDisabled={isSaving || isLoading || (isEditMode && editTaskInternalId > 0)}
                />
            </div>
        </>
    );

    return (
        <Sidebar
            content={sidebarContent}
            visible={props.isDrawerVisible}
            position="right"
            onShow={onShowSidebar}
            onHide={onHideSidebar}
            className="col-12 col-lg-8 col-xl-7"
        />
    );
};

export default QuickCreateTaskSidebar;
